import {
    Box,
    Theme,
    ThemeProvider,
    createTheme
} from "@mui/material";
import {
    memo,
    useEffect,
    useState
} from "react";

import { AwaitPropertiesChildrenSyncedWrapper } from "../../components/sync/AwaitPropertiesChildrenSyncedWrapper";
import Inspection from "../../componentsV2/inspection/Inspection";
import { MEETING_DRAWER_WIDTH } from "../../components/chime/LiveInspectionDrawer";
import { PropertyArchivedAlert } from "../../componentsV2/property/PropertyArchivedAlert";
import PropertyInfo from "../../componentsV2/property/PropertyInfo";
import { PropertyTab } from "../../lib/property/v2/PropertyTab";
import PropertyTabs from "../../componentsV2/property/PropertyTabs";
import { ViewPropertySolutions } from "../../components/solution/ViewPropertySolutions";
import { WorkTypeFetchWrapper } from "../../components/WorkTypeFetchWrapper";
import globalStyle from "../../assets/style/globalStyle";
import { isMeetingDrawerOpenAtom } from "../../lib/sidebar/SidebarRecoilStates";
import loadable from "@loadable/component";
import { usePageDefaultStyle } from "../../assets/style/usePageDefaultStyle";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

type PropertyDetailsParams = {
    readonly propertyId: string;
};

const LiveInspectionDrawer = loadable(async () => {
    const { LiveInspectionDrawer } = await import("../../components/chime/LiveInspectionDrawer");
    return LiveInspectionDrawer;
});

const meetingDrawerTheme = (theme: Theme) => createTheme({
    ...theme,
    breakpoints: {
        values: {
            xs: theme.breakpoints.values.xs + MEETING_DRAWER_WIDTH,
            sm: theme.breakpoints.values.sm + MEETING_DRAWER_WIDTH,
            md: theme.breakpoints.values.md + MEETING_DRAWER_WIDTH,
            lg: theme.breakpoints.values.lg + MEETING_DRAWER_WIDTH,
            xl: theme.breakpoints.values.xl + MEETING_DRAWER_WIDTH
        }
    },
});

const PropertyDetails = () => {
    /* Internal page state */
    const { propertyId } = useParams<PropertyDetailsParams>();

    /* PropertyTabs state */
    const [activeTab, setActiveTab] = useState<PropertyTab>(() => {
        const currentTabName: string | undefined = window.location.pathname.split("/").pop();
        switch (currentTabName) {
            case PropertyTab.DETAILS:
                return PropertyTab.DETAILS;
            case PropertyTab.ISSUES:
                return PropertyTab.ISSUES;
            case PropertyTab.SOLUTIONS:
                return PropertyTab.SOLUTIONS;
            default:
                return PropertyTab.DETAILS;
        }
    });

    /* Dependencies */
    const globalClasses = globalStyle();
    const { pageDefaultStyle } = usePageDefaultStyle();
    const isMeetingDrawerOpen = useRecoilValue(isMeetingDrawerOpenAtom);

    useEffect(() => {
        // persist tab state in URL
        const tabName = activeTab.toString();
        if (window.location.pathname.endsWith(tabName)) {
            return;
        }
        // replace the last part of the URL with the tab name
        const newPathname = `/properties/${propertyId}/${tabName}`;
        const searchParams = window.location.search;
        window.history.replaceState({}, "", `${newPathname}${searchParams}`);
    }, [activeTab]);

    return (
        <AwaitPropertiesChildrenSyncedWrapper>
            <WorkTypeFetchWrapper>
                <ThemeProvider theme={isMeetingDrawerOpen ? meetingDrawerTheme : theme => theme}>
                    <Box
                        pr={isMeetingDrawerOpen ? `${MEETING_DRAWER_WIDTH}px` : undefined}
                        pb={isMeetingDrawerOpen ? "80px" : undefined}
                    >
                        <Box pt={1}>
                            <PropertyTabs
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />
                        </Box>
                        <Box sx={pageDefaultStyle}>
                            <PropertyArchivedAlert propertyId={propertyId} />
                            <Box className={activeTab !== PropertyTab.DETAILS ? globalClasses.hidden : ""}>
                                <PropertyInfo propertyId={propertyId} />
                            </Box>
                            <Box className={activeTab !== PropertyTab.ISSUES ? globalClasses.hidden : ""}>
                                <Inspection propertyId={propertyId} />
                            </Box>
                            <Box className={activeTab !== PropertyTab.SOLUTIONS ? globalClasses.hidden : ""}>
                                <ViewPropertySolutions />
                            </Box>
                        </Box>
                    </Box>
                    <LiveInspectionDrawer />
                </ThemeProvider>
            </WorkTypeFetchWrapper>
        </AwaitPropertiesChildrenSyncedWrapper >
    );
};

export default memo(PropertyDetails);
